import { z } from 'zod'
import { omit } from 'lodash'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { firebaseRtdb } from '@/lib/firebase'
import { formatDistanceToNow } from 'date-fns'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { BadgeCheck, Reply, Trash2, UserX, X } from 'lucide-react'
import { ref, onValue, push, off, remove } from 'firebase/database'
import { useAuthContext } from '@/contexts/auth-context/auth-provider'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface CommentFormValues {
  content: string
}

interface LiveComment {
  id?: string
  avatar: string
  content: string
  name: string
  timestamp: number
  userId: string
  replyTo?: LiveComment | null
  isProfileVerified?: boolean
  role: string
}

const schema = z.object({
  content: z.string().min(1, 'กรุณาใส่ความคิดเห็น'),
})

export type Props = {
  programId?: string
}

export const LiveCommentSection: React.FC<Props> = ({ programId }) => {
  const [comments, setComments] = useState<LiveComment[]>([])
  const { user } = useAuthContext()
  const isLoading = useBoolean(true)
  const [replyTo, setReplyTo] = useState<LiveComment | null>(null)
  const [selectedDelete, setSelectedDelete] = useState<LiveComment | null>(null)
  const [selectedBan, setSelectedBan] = useState<LiveComment | null>(null)
  const openConfirmDeleteDialog = useBoolean(false)
  const openBanOptionDialog = useBoolean(false)

  const isDeleting = useBoolean(false)
  const isBanning = useBoolean(false)

  const form = useForm<CommentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { content: '' },
  })

  useEffect(() => {
    if (!programId) return

    const commentsRef = ref(firebaseRtdb, `live/${programId}/comments`)

    const unsubscribe = onValue(
      commentsRef,
      (snapshot) => {
        const data = snapshot.val()
        if (data) {
          const commentsArray = Object.keys(data)
            .map((key) => ({
              id: key,
              ...data[key],
            }))
            .sort((a, b) => b.timestamp - a.timestamp) // เรียงจากใหม่ไปเก่า
            .slice(0, 80)

          setComments(commentsArray)
        } else {
          setComments([])
        }
        isLoading.onFalse()
      },
      (error) => {
        console.error('Error fetching live comments:', error)
        toast.error('ไม่สามารถโหลดความคิดเห็นได้')
        isLoading.onFalse()
      },
    )

    return () => {
      off(commentsRef, 'value', unsubscribe)
    }
  }, [programId])

  const onSubmit = async (values: CommentFormValues) => {
    if (!programId || !user) return

    try {
      const commentsRef = ref(firebaseRtdb, `live/${programId}/comments`)

      const newComment: LiveComment = {
        avatar: user?.imageUrl || '',
        content: values.content,
        name:
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.displayName || user.email || 'ผู้ใช้',
        timestamp: Date.now(),
        userId: user.uid,
        replyTo: omit(replyTo, ['replyTo']),
        role: user.role,
      }

      await push(commentsRef, newComment)
      form.reset()
    } catch (error) {
      console.error('Error sending comment:', error)
      toast.error('ไม่สามารถส่งความคิดเห็นได้')
    } finally {
      setReplyTo(null)
    }
  }

  const handleDeleteComment = async () => {
    if (!programId || !selectedDelete?.id) return

    try {
      isDeleting.onTrue()
      const commentRef = ref(
        firebaseRtdb,
        `live/${programId}/comments/${selectedDelete.id}`,
      )
      await remove(commentRef)
      toast.success('ลบความคิดเห็นสำเร็จ')
      openConfirmDeleteDialog.onFalse()
      setSelectedDelete(null)
    } catch (error) {
      console.error('Error deleting comment:', error)
      toast.error('ไม่สามารถลบความคิดเห็นได้')
    } finally {
      isDeleting.onFalse()
    }
  }

  const handleBanUser = async (duration: number) => {
    if (!selectedBan?.userId || selectedBan.userId === user?.uid) return

    try {
      isBanning.onTrue()
      await axios.post(endpoints.users.ban(selectedBan.userId), {
        time: duration,
      })
      toast.success('แบนผู้ใช้สำเร็จ')
      openBanOptionDialog.onFalse()
      setSelectedBan(null)
    } catch (error) {
      console.error('Error banning user:', error)
      toast.error('ไม่สามารถแบนผู้ใช้ได้')
    } finally {
      isBanning.onFalse()
    }
  }

  if (isLoading.value) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted">กำลังโหลด...</p>
        </div>
      </div>
    )
  }

  // console.log(comments)

  return (
    <div className="mt-2">
      <p className="text-base font-bold ">ความคิดเห็น</p>
      <div className="bg-background rounded-lg mb-6">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex bg-background gap-2 "
          >
            <div className="flex-1">
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="พิมพ์ความคิดเห็นของคุณ..."
                        {...field}
                        className="h-12 text-sm z-10 bg-background"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit" size="lg" className="h-12">
              ส่ง
            </Button>
          </form>
        </Form>
        {replyTo && (
          <div className="bg-background rounded-lg px-3 pb-2 pt-4.5 border border-neutral-700 w-[calc(100%-80px)] -mt-3 z-9 space-y-1">
            <div className="flex items-center justify-between">
              <p className="text-xs max-w-[calc(100%)] line-clamp-1">
                ตอบกลับไปยัง: <b className="text-sm">{replyTo?.name}</b>
              </p>
              <Button
                variant="ghost"
                size="icon"
                className="size-4"
                onClick={() => setReplyTo(null)}
              >
                <X />
              </Button>
            </div>
            <p className="text-sm line-clamp-2">{replyTo?.content}</p>
          </div>
        )}
      </div>

      <div className="space-y-3.5">
        {comments.map((c) => {
          const avatar = c.role === 'ADMIN' ? '/images/rfc-logo.png' : c.avatar
          const displayName = c.role === 'ADMIN' ? 'RFC Official' : c.name
          const isAdmin = c.role === 'ADMIN'
          return (
            <div key={c.id} className="bg-background rounded-lg space-y-3">
              <div className="flex items-start space-x-3">
                <Avatar>
                  <AvatarImage
                    className="object-cover"
                    src={avatar}
                    alt="avatar"
                  />
                  <AvatarFallback>
                    {displayName ? displayName.slice(0, 2) : ''}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-end">
                      <div className="flex items-center ">
                        <h4 className="text-sm font-medium line-clamp-1 max-w-[180px]">
                          {displayName}
                        </h4>
                        {(!!c.isProfileVerified || isAdmin) && (
                          <BadgeCheck className="h-3.5 w-3.5 text-blue-500 stroke-[2.5px] ml-1 mb-0.5" />
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground ml-2">
                        {c?.timestamp
                          ? formatDistanceToNow(new Date(c.timestamp), {
                              addSuffix: false,
                            })
                          : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Reply Comment */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-4"
                        onClick={() => setReplyTo(c)}
                      >
                        <Reply />
                      </Button>

                      {/* Delete Comment */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-4 hover:text-red-500"
                        onClick={() => {
                          setSelectedDelete(c)
                          openConfirmDeleteDialog.onTrue()
                        }}
                      >
                        <Trash2 />
                      </Button>

                      {/* Ban User */}
                      {c.userId !== user?.uid && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-4 hover:text-red-500"
                          onClick={() => {
                            setSelectedBan(c)
                            openBanOptionDialog.onTrue()
                          }}
                        >
                          <UserX />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Reply To Section */}
                  {c.replyTo && (
                    <div className="border-l-2 border-primary/50 pl-3 py-ๅ mb-2 rounded-r-lg">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Reply className="size-3 text-primary" />
                        <span className="text-xs text-muted-foreground">
                          ตอบกลับ
                        </span>
                        <span className="text-xs font-medium text-primary">
                          {c.replyTo.role === 'ADMIN'
                            ? 'RFC Official'
                            : c.replyTo.name}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 break-words">
                        {c.replyTo.content}
                      </p>
                    </div>
                  )}

                  <p className="text-sm break-words">{c.content}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Confirm Delete */}
      <ConfirmDialog
        open={openConfirmDeleteDialog.value}
        onOpenChange={openConfirmDeleteDialog.onFalse}
        title="ลบความคิดเห็น"
        description="คุณต้องการลบความคิดเห็นนี้หรือไม่"
        onConfirm={handleDeleteComment}
        loading={isDeleting.value}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
      />

      {/* Ban Option Dialog */}
      <BanOptionDialog
        open={openBanOptionDialog.value}
        onOpenChange={openBanOptionDialog.onFalse}
        onConfirm={handleBanUser}
        userName={selectedBan?.name}
        loading={isBanning.value}
      />
    </div>
  )
}

interface BanOptionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (duration: number) => void
  userName?: string
  loading?: boolean
}

function BanOptionDialog({
  open,
  onOpenChange,
  onConfirm,
  userName,
  loading = false,
}: BanOptionDialogProps) {
  const [selectedDuration, setSelectedDuration] = useState<number>(0)

  const banOptions = [
    { label: '15 นาที', value: 60 * 15 },
    { label: '30 นาที', value: 60 * 30 },
    { label: '1 วัน', value: 60 * 60 * 24 },
    { label: '7 วัน', value: 60 * 60 * 24 * 7 },
    { label: '30 วัน', value: 60 * 60 * 24 * 30 },
    { label: 'ถาวร', value: 60 * 60 * 24 * 365 * 20 }, // 20 years
  ]

  const handleConfirm = () => {
    if (selectedDuration > 0) {
      onConfirm(selectedDuration)
    }
  }

  const handleCancel = () => {
    setSelectedDuration(0)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="" className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>แบนผู้ใช้</DialogTitle>
          <DialogDescription>
            เลือกระยะเวลาแบนสำหรับ {userName || 'ผู้ใช้นี้'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-3">
            {banOptions.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <input
                  type="radio"
                  id={`ban-${option.value}`}
                  name="banDuration"
                  value={option.value}
                  checked={selectedDuration === option.value}
                  onChange={() => setSelectedDuration(option.value)}
                  className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                />
                <label
                  htmlFor={`ban-${option.value}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            ยกเลิก
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={selectedDuration === 0 || loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                กำลังดำเนินการ...
              </div>
            ) : (
              'แบนผู้ใช้'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
